/**
 * §6.5 Brand Customization — before/after column showing every
 * customisable surface (name, logo, palette, typography, email,
 * domain, favicon, PWA icon, login screen, etc.).
 */

import React from 'react'
import { Palette, ArrowRight, Sparkles } from 'lucide-react'
import { Container, Eyebrow, SectionHeading } from '@/components/landing/design-system'
import { BRAND_FIELDS } from '../data'

export default function Branding() {
  return (
    <section className="relative py-24 md:py-28">
      <Container size="wide" className="flex flex-col gap-10">
        <div className="flex flex-col items-center text-center gap-5 max-w-3xl mx-auto">
          <Eyebrow>Brand Customization</Eyebrow>
          <SectionHeading align="center"
            title={<>Every visible surface,{' '}<span className="bg-gradient-to-r from-brand-gold to-[#FFE17A] bg-clip-text text-transparent">under your brand.</span></>}
            subtitle="Nothing says Kalki Ai Cloud on your platform. Nine control points — from company name to email sender — are tokenised per tenant."
          />
        </div>

        <div className="relative rounded-3xl border border-white/10 bg-gradient-to-br from-white/[0.03] to-white/[0.01] p-4 md:p-6 backdrop-blur-xl overflow-hidden">
          <div className="pointer-events-none absolute inset-0 opacity-60"
            style={{ background: 'radial-gradient(60% 40% at 20% 0%, rgba(255,193,7,0.10), transparent 70%)' }} />

          {/* header row */}
          <div className="relative hidden md:grid grid-cols-12 gap-3 px-2 pb-3 mb-1 border-b border-white/5">
            <div className="col-span-4 text-[10.5px] font-mono uppercase tracking-wider text-zinc-500">Surface</div>
            <div className="col-span-4 text-[10.5px] font-mono uppercase tracking-wider text-zinc-500">Before</div>
            <div className="col-span-4 text-[10.5px] font-mono uppercase tracking-wider text-brand-gold">After (yours)</div>
          </div>

          <div className="relative flex flex-col gap-2">
            {BRAND_FIELDS.map((b, idx) => (
              <div key={b.id} className="grid grid-cols-1 md:grid-cols-12 gap-3 items-center rounded-xl border border-white/5 bg-white/[0.02] px-3 md:px-4 py-3">
                <div className="col-span-4 flex items-center gap-2">
                  <span className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-white/10 bg-white/[0.03] text-zinc-400 text-[10.5px] font-mono">
                    {String(idx + 1).padStart(2, '0')}
                  </span>
                  <span className="text-[13.5px] font-semibold text-white leading-tight">{b.label}</span>
                </div>
                <div className="col-span-4 flex items-center gap-2 text-[13px] text-zinc-500 line-through decoration-white/20">
                  {b.valueFrom}
                </div>
                <div className="col-span-4 flex items-center gap-2">
                  <ArrowRight className="h-3.5 w-3.5 text-brand-gold shrink-0" />
                  <span className="text-[13px] font-semibold text-white">{b.valueTo}</span>
                </div>
              </div>
            ))}
          </div>

          {/* callout */}
          <div className="relative mt-4 flex flex-col md:flex-row items-start md:items-center gap-3 rounded-2xl border border-brand-gold/25 bg-brand-gold/[0.05] px-4 md:px-5 py-3">
            <Palette className="h-4 w-4 text-brand-gold shrink-0" />
            <p className="text-[13.5px] text-zinc-100 leading-relaxed">
              Colour palette, typography and email templates are versioned per tenant —
              rollback is a single click. Change tokens without redeploying.
            </p>
            <span className="ml-auto inline-flex items-center gap-1 text-[10.5px] font-mono text-emerald-400">
              <Sparkles className="h-3 w-3" /> ~seconds to apply
            </span>
          </div>
        </div>
      </Container>
    </section>
  )
}
